import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Row } from "@tanstack/react-table";
import { Order, Product } from "types";
import Select, { components } from "react-select";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { formatPaymentMethodLabel } from "../../utils/paymentMethodLabel";

interface ExportRow {
  "Order ID": string;
  "Order Date": string;
  "Order Status": string;
  "Payment Status": string;
  "Buyer Type": string;
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Billing Address": string;
  "Shipping Address": string;
  "Item Details": string;
  "Customization Information": string;
  "Shipping Method": string;
  "Sub Total": number;
  "Shipping": number;
  "Tax": number;
  "Total": number;
}

type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Order>();
const DATE_OPTIONS = [
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};
const RealtorOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  // const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [productionStatusFilter, setProductionStatusFilter] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.orders
  );
  const navigate = useNavigate();

  function getOrderIdForNavigation(order: Order): string {
    const o = order as any;
    return String(o?.id ?? o?._id ?? o?.order_id ?? o?.orderId ?? "");
  }

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [datePreset, setDatePreset] = useState<(typeof DATE_OPTIONS)[number] | null>(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);
    return {
      minDate: sixMonthsAgo.toISOString().split("T")[0],
      maxDate: now.toISOString().split("T")[0],
    };
  }, []);

  useEffect(() => {
    if (datePreset?.value === "custom") {
      if (customFrom && (customFrom < minDate || customFrom > maxDate)) setCustomFrom("");
      if (customTo && (customTo < minDate || customTo > maxDate)) setCustomTo("");
    }
  }, [datePreset?.value, minDate, maxDate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const hasSearch = Boolean(globalFilter?.trim());
      const dateRange = hasSearch || !datePreset
        ? { type: "", from: "", to: "" }
        : {
            type: datePreset.value,
            from: datePreset.value === "custom" ? customFrom : "",
            to: datePreset.value === "custom" ? customTo : "",
          };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/orders/user/datatable`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          search: globalFilter,
          // linked_status: "accepted",
          // current_status: "in_transit",
          order_status: orderStatusFilter,
          payment_status: paymentStatusFilter,
          production_status: productionStatusFilter
            ? productionStatusFilter.toLowerCase().replace(/\s+/g, "_")
            : "",
          date_range: dateRange,
          sort: {
            field: "created_at",
            order: "desc"
          },
          // fields: ["firstName", "lastName", "email", "jobTitle", "warehouses", "status", "created_at"]
        })
      });

      if (!response.ok) throw new Error("Failed to fetch orders data");

      const result = await response.json();

      // Ensure `data` and `total` are present and fallback to defaults if not
      setOrders(Array.isArray(result?.data) ? result.data : []);
      setFilteredOrders(Array.isArray(result?.data) ? result.data : []);
      setRowCount(typeof result?.total === 'number' ? result.total : 0);

    } catch (err) {
      console.error("Error fetching orders data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchOrders();
  }, [userId, token, pagination.pageIndex, pagination.pageSize, globalFilter]);

  // useEffect(() => {
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);

  //   const filtered = orders?.filter((order) => {
  //     const orderDate = new Date(order.created_at);
  //     const matchesSearch =
  //       order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       order.customer_first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //       order.customer_last_name.toLowerCase().includes(searchQuery.toLowerCase());

  //     // const matchesStatus = statusFilter === "all" || String(order.status) === statusFilter;
  //     const matchesOrderStatus = orderStatusFilter === "all" || String(order.status) === orderStatusFilter;
  //     const matchesProductionStatus = productionStatusFilter === "all" || order.items[0].production_status === productionStatusFilter;
  //     const matchesPaymentStatus = paymentStatusFilter === "all" || order.payment_status === paymentStatusFilter;

  //     let matchesDate = true;
  //     if (dateFilter === "today") {
  //       matchesDate = orderDate >= today && orderDate < new Date(today.getTime() + 86400000);
  //     } else if (dateFilter === "yesterday") {
  //       const yesterday = new Date(today.getTime() - 86400000);
  //       matchesDate = orderDate >= yesterday && orderDate < today;
  //     } else if (dateFilter === "custom") {
  //       const start = customStartDate ? new Date(customStartDate) : null;
  //       const end = customEndDate ? new Date(customEndDate) : null;
  //       matchesDate = (!start || orderDate >= start) && (!end || orderDate <= end);
  //     }

  //     // return matchesSearch && matchesStatus && matchesDate;
  //     return matchesSearch && matchesOrderStatus && matchesProductionStatus && matchesPaymentStatus && matchesDate;

  //   });

  //   setFilteredOrders(filtered);
  // }, [searchQuery, orderStatusFilter, productionStatusFilter, paymentStatusFilter, dateFilter, customStartDate, customEndDate, orders]);
  const columns = useMemo(() => [
    columnHelper.accessor("order_number", {
      id: "order_number",
      header: () => <div className="th-text S-No">Order ID</div>,
      cell: (info) => {
        const row = info.row.original as Order;
        // pick whichever your data uses:
        const orderId =
          (row as any).id ?? (row as any)._id ?? (row as any).order_id;

        return (
          <Link
            to={`${baseRoute}/orderDetails/${orderId}`}
            className="text-sm S-No order-link-1"
          >
            {info.getValue() ?? ""}
          </Link>
        );
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    // columnHelper.accessor(
    //   (row: Order) => `REO${row.order_number}`,
    //   {
    //     id: "order_number",
    //     header: () => <div className="th-text S-No">Order ID</div>,
    //     cell: info => <div className="text-sm order-link-1 S-No">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }),
    columnHelper.accessor(
      (row: Order) => new Date(row.created_at).toLocaleDateString("en-US"),
      {
        id: "created_at",
        header: () => <div className="th-text">Order Date</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: Order) => row.brokerage_firm_name || "-",
      {
        id: "brokerage_firm_name",
        header: () => (
          <div className="th-text ">Brokerage Firm</div>
        ),
        cell: (info) => (
          <div className="text-sm wrap-anywhere">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: Order) => row.office?.officeName || "-",
      {
        id: "officeName",
        header: () => (
          <div className="th-text">Office</div>
        ),
        cell: (info) => (
          <div className="text-sm wrap-anywhere">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: Order) => row.customer_email || "-",
      {
        id: "customer_email",
        header: () => (
          <div className="th-text">Email</div>
        ),
        cell: (info) => (
          <div className="text-sm wrap-anywhere">{info.getValue()}</div>
        ),
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.accessor(
      (row: Order) => {
        // show name only if agent_id is present
        if (!row?.agent_id) return "--";
        const fn = (row?.customer_first_name ?? "").trim();
        const ln = (row?.customer_last_name ?? "").trim();
        const full = [fn, ln].filter(Boolean).join(" ");
        return full || "--";
      },
      {
        id: "customer_name",
        header: () => <div className="th-text">Agent</div>,
        cell: (info) => {
          const val = info.getValue<string>();
          const isMissing = val === "--";
          return (
            <div className={`text-sm ${isMissing ? "text-muted" : ""}`}>
              {val}
            </div>
          );
        },
        filterFn: fuzzyTextFilter,       // still works for names; "--" won't match
        enableGlobalFilter: true,
        sortingFn: "alphanumeric",       // optional: sensible sorting
      }
    ),
    // columnHelper.accessor(
    //   (row: Order) => row.office?.officeName || "",
    //   {
    //     id: "officeName",
    //     header: () => <div className="th-text">Office Name</div>,
    //     cell: info => <div className="text-sm">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }),
    // columnHelper.accessor(
    //   (row: Order) => `${row.customer_first_name || ""} ${row.customer_last_name || ""}`,
    //   {
    //     id: "customer_name",
    //     header: () => <div className="th-text">Agent</div>,
    //     cell: info => <div className="text-sm">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }),

    // columnHelper.accessor(
    //   (row: Order) => row.items[0].production_status || "-",
    //   {
    //     id: "production_status",
    //     header: () => <div className="th-text">Production Status</div>,
    //     cell: info => <div className="text-sm">{info.getValue()}</div>,
    //     filterFn: fuzzyTextFilter,
    //     enableGlobalFilter: true,
    //   }),
        columnHelper.accessor("payment_status", {
      header: () => (
        <div className="th-text">Payment Status</div>
      ),
      cell: (info) => {
        const paymentStatus = info.getValue() as string;
        return (
          <div className="text-sm">
            {paymentStatus ? (
              <SalesOrderStatusBadge type="payment" value={paymentStatus} />
            ) : (
              "-"
            )}
          </div>
        );
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor("status", {
      header: () => (
        <div className="th-text">Order Status</div>
      ),
      cell: (info) => {
        const orderStatus = info.getValue() as number;
        return (
          <div className="text-sm">
            {orderStatus ? (
              <SalesOrderStatusBadge type="order" value={orderStatus} />
            ) : (
              "-"
            )}
          </div>
        );
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor(
      (row: Order) => formatPaymentMethodLabel(row.payment_method),
      {
        id: "payment_method",
        header: () => <div className="th-text">Payment Method</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    // columnHelper.display({
    //   id: "status",
    //   header: () => <div className="th-text">Order Status</div>,
    //   cell: (info) => {
    //     const row = info.row.original;
    //     return (
    //       <div className="text-sm">
    //         {row.status === 100 ? <span className="status-badge status-pending-btn">Draft</span> :
    //           row.status === 101 ? <span className="status-badge status-accepted-btn">Confirmed</span> :
    //           row.status === 300 ? <span className="status-badge status-shipped-btn">Shipping</span> :
    //           row.status === 301 ? <span className="status-badge status-shipped-btn">Shipped</span> :
    //           row.status === 302 || row.status === 401 ? <span className="status-badge status-delivered-btn">Delivered</span> :
    //           row.status === 400 ? <span className="status-badge status-canceled-btn">Cancelled</span> : "-"}
    //       </div>
    //     );
    //   },
    //   filterFn: fuzzyTextFilter,
    //   enableGlobalFilter: true,
    // }),
    // columnHelper.display({
    //   id: "payment_status",
    //   header: () => <div className="th-text">Payment Status</div>,
    //   cell: (info) => {
    //     const row = info.row.original;
    //     return (
    //       <div className="text-sm">
    //         {row.payment_status === "Partial Payment" ? <span className="status-badge status-pending-btn">Partial Payment</span> :
    //           row.payment_status === "Paid" ? <span className="status-badge status-accepted-btn">Paid</span> :
    //           row.payment_status === "Pending" ? <span className="status-badge status-pending-btn">Pending</span> : "-"}
    //       </div>
    //     );
    //   },
    //   filterFn: fuzzyTextFilter,
    //   enableGlobalFilter: true,
    // }),
    columnHelper.accessor(
      (row: Order) => row.signage_company_name || "-",
      {
        id: "vendor",
        header: () => <div className="th-text">Vendor</div>,
        cell: info => <div className="text-sm wrap-anywhere">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const order = info.row.original;
        if (!canView && !canEdit && !canDelete) return null;
        return (
          <div className="d-flex align-items-center gap-2">
            {canView && (
              <ViewIconButton
                to={`${baseRoute}/orderDetails/${order.id}`} className="btn action-btn fs-2 text-center"
              />
            )}
            {canEdit && !((order?.status ?? 0) >= 101) && (
              <EditIconButton
                to={`${baseRoute}/order/checkout`} className="icon-square me-3" state={{ orderId: order.id }} />
            )}
          </div>
          // <div className="text-center  table-cells d-flex justify-content-center align-items-center gap-2">
          //   <Link
          //     to={`${baseRoute}/orderDetails/${order.id}`} 
          //     className="view me-3"
          //     title="View"
          //   >
          //     <i className="ri-eye-fill fs-20"></i>
          //   </Link>
          //   <Link
          //     to={`${baseRoute}/order/checkout`}
          //     state={{ orderId: order.id }}
          //     className="me-3 edit"
          //   >
          //     <i className="ri-pencil-fill fs-20"></i>
          //   </Link>

          // </div>
        );
      },
    }),
  ], [deleting, canView, canEdit, canDelete]);

  useEffect(() => {
    // Order status mapping function
    const getOrderStatusText = (status: number) => {
      switch (status) {
        case 100: return "Draft";
        case 101: return "Confirmed";
        case 300: return "Shipping";
        case 301: return "Shipped";
        case 302: return "Delivered";
        case 400: return "Cancelled";
        default: return "";
      }
    };

    // Get full address string from address_details
    const getFullAddress = (address: any) => {
      if (!address || !address.address_details) return "";
      const addr = address.address_details;
      return [
        addr?.addressLine1,
        addr?.addressLine2,
        addr?.city,
        addr?.state,
        addr?.zip,
        addr?.country,
      ].filter(Boolean).join(', ');
    };

    const rows: ExportRow[] = filteredOrders?.map(order => ({
      "Order ID": order?.order_number || '',
      "Order Date": order?.created_at ? new Date(order.created_at).toLocaleDateString('en-GB').replace(/\//g, '-') : '',
      "Order Status": getOrderStatusText(order?.status || 0),
      "Payment Status": order?.payment_status || '',
      "Buyer Type": order?.buyer_type || '',
      "First Name": order?.customer_first_name || '',
      "Last Name": order?.customer_last_name || '',
      "Email": order?.customer_email || '',
      "Phone": order?.customer_phone || '',
      "Billing Address": getFullAddress(order?.billing_address),
      "Shipping Address": getFullAddress(order?.shipping_address),
      "Item Details": order?.items?.map(item => item.product_name).join(', ') || '',
      "Customization Information": "",
      "Shipping Method": order?.shipping_method?.carrier_name || order?.manual_shipping_method?.name || '',
      "Sub Total": order?.total_amount - order?.tax_amount - order?.shipping_amount || 0,
      "Shipping": order?.shipping_amount || 0,
      "Tax": order?.tax_amount || 0,
      "Total": order?.total_amount || 0,
    }));

    setExportRows(rows);
  }, [filteredOrders]);

  const table = useReactTable({
    data: filteredOrders,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content">

      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 ">
        {/* <h4 className="mb-0 fw-bold">Orders</h4> */}
        <h4></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/suppliers`} className="btn btn-primary  table-addbutton">
              + Create Order
            </Link>
          )}

        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Orders</h4>
            {/* <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Product Name, SKU, Brokerages…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <ExportAsExcel
              data={exportRows}
              headers={[
                "Order ID",
                "Order Date",
                "Order Status",
                "Payment Status",
                "Buyer Type",
                "First Name",
                "Last Name",
                "Email",
                "Phone",
                "Billing Address",
                "Shipping Address",
                "Item Details",
                "Customization Information",
                "Shipping Method",
                "Sub Total",
                "Shipping",
                "Tax",
                "Total",
              ]}
              fileName="Orders"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>

          </div>
        </div>

        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex"> {/* <-- flex wrapper */}
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-12 col-md-auto">
                <label htmlFor="linkedStatusFilter" className="form-label mb-1 label-1">Order Status</label>
                <select
                  className="form-select "
                  value={orderStatusFilter}
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                >
                  <option value="">Order Status</option>
                  <option value="100">Draft</option>
                  <option value="101">Confirmed</option>
                  {/* <option value="300">Shipping</option> */}
                  <option value="301">Shipped</option>
                  <option value="302">Delivered</option>
                  <option value="400">Cancelled</option>
                </select>
              </div>

              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Production Status</label>
                <select
                  className="form-select "
                  value={productionStatusFilter}
                  onChange={(e) => setProductionStatusFilter(e.target.value)}
                >
                  <option value="">Production Status</option>
                  <option value="Not Started">Not Started</option>
                  <option value="In Production">In Production</option>
                  <option value="Quality Check">Quality Check</option>
                  <option value="Ready for Installation">Ready for Installation</option>
                </select>
              </div>
              <div className="col-12 col-md-auto">
                <label htmlFor="currentStatusFilter" className="form-label mb-1 label-1">Payment Status</label>
                <select
                  className="form-select "
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                >
                  <option value="">Payment Status</option>
                  <option value="Partial Payment">Partial Payment</option>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
              <div className="col-12 col-md-auto">
                <label className="form-label">Date Range</label>
                <Select
                  options={DATE_OPTIONS}
                  value={datePreset}
                  onChange={(val) => setDatePreset(val ?? null)}
                  placeholder="Select date range"
                  isClearable
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                  classNamePrefix="rs"
                />
              </div>
              {datePreset?.value === "custom" && (
                <>
                  <div className="col-md-2">
                    <label className="form-label">From</label>
                    <input type="date" className="form-control br-8" value={customFrom}
                      min={minDate} max={maxDate}
                      onChange={e => setCustomFrom(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">To</label>
                    <input type="date" className="form-control br-8" value={customTo}
                      min={minDate} max={maxDate}
                      onChange={e => setCustomTo(e.target.value)} />
                  </div>
                </>
              )}
              <div className={`col-12 col-md-auto d-flex justify-content-md-end gap-2 ${datePreset?.value === "custom" ? "mt-3" : "mt-1 mt-md-0"}`}>
                <button
                  className="btn btn-primary table-addbutton flex-fill flex-md-grow-0"
                  onClick={fetchOrders}
                >
                  <i className="ri-check-line me-1" />
                  Apply Filter
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-lg-grow-0"
                  onClick={() => {
                    setOrderStatusFilter("");
                    setProductionStatusFilter("");
                    setPaymentStatusFilter("");
                    setDatePreset(null);
                    setCustomFrom("");
                    setCustomTo("");
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 ">Sales Orders</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* orders */}
          </span>

        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts
                table={table}
                onRowClick={(row) => {
                  const orderId = getOrderIdForNavigation(row);
                  if (!orderId) return;
                  navigate(`${baseRoute}/orderDetails/${orderId}`);
                }}
              />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile shadow-sm">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
            onRowClick={(row) => {
              const orderId = getOrderIdForNavigation(row);
              if (!orderId) return;
              navigate(`${baseRoute}/orderDetails/${orderId}`);
            }}
          />
        </div>
      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>)}
    </div>
  );
};

export default RealtorOrders;




// const columns = useMemo(() => {
//   return [
//     columnHelper.accessor(
//       (row: Order) => `REO${row.order_number}`,
//       {
//         header: "Order ID",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: Order) => new Date(row.created_at).toLocaleDateString("en-US"),
//       {
//         header: "Order Date",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: Order) => row.office?.officeName || "",
//       {
//         header: "Office Name",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: Order) => `${row.customer_first_name || ""} ${row.customer_last_name || ""}`,
//       {
//         header: "Agent",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.accessor(
//       (row: Order) => row.items[0].production_status || "-",
//       {
//         header: "Production Status",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),
//     columnHelper.display({
//       id: "status",
//       header: "Order Status",
//       cell: (info) => {
//         const row = info.row.original;
//         return row.status === 100 ? <span className="status-badge status-pending-btn">Draft</span> :
//           row.status === 101 ? <span className="status-badge status-accepted-btn">Confirmed</span> :
//             row.status === 300 ? <span className="status-badge status-shipped-btn">Shipping</span> :
//               row.status === 301 ? <span className="status-badge status-shipped-btn">Shipped</span> :
//                 row.status === 302 || row.status === 401 ? <span className="status-badge status-delivered-btn">Delivered</span> :
//                   row.status === 400 ? <span className="status-badge status-canceled-btn">Cancelled</span> : "-";
//       },
//       filterFn: fuzzyTextFilter,
//       enableGlobalFilter: true,
//     }),
//     columnHelper.display({
//       id: "payment_status",
//       header: "Payment Status",
//       cell: (info) => {
//         const row = info.row.original;
//         return row.payment_status === "Partial Payment" ? <span className="status-badge status-pending-btn">Partial Payment</span> :
//           row.payment_status === "Paid" ? <span className="status-badge status-accepted-btn">Paid</span> :
//             row.payment_status === "Pending" ? <span className="status-badge status-pending-btn">Pending</span> : "-"
//       },
//       filterFn: fuzzyTextFilter,
//       enableGlobalFilter: true,
//     }),
//     columnHelper.accessor(
//       (row: Order) => row.seller_name || "-",
//       {
//         header: "Vendor",
//         filterFn: fuzzyTextFilter,
//         enableGlobalFilter: true,
//       }),

//     columnHelper.display({
//       id: "actions",
//       header: "Actions",
//       cell: (info) => {
//         const order = info.row.original;
//         return (
//           <>
//             <Link
//               to={`${baseRoute}/orderDetails/${order.id}`}
//               className="view"
//               title="View"
//             >
//               <i className="ri-eye-fill fs-20"></i>
//             </Link>
//           </>
//         );
//       },
//     }),
//   ];
// }, [deleting]);